import { useSyncExternalStore } from 'react';

/**
 * LLAMS-465 — session-level record that the CRM remote reported the user's license as revoked
 * (`crm:unlicensed`, i.e. CRMAPI `403 crm.license_required`) while the host still holds a token whose
 * `CrmLicense` claim was read at login.
 *
 * Keyed to the JWT that received the 403: a new login (new token) — even for the same user after
 * the license is restored — is never treated as revoked, with no page reload required.
 *
 * Module-scope store + `useSyncExternalStore`, same style as the `crmPortal` singleton in CrmRoutes.
 */

let revokedToken: string | null = null;
const listeners = new Set<() => void>();

function notifyListeners(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): string | null {
  return revokedToken;
}

/** Records that `token` (the host token current when the 403 happened) is no longer CRM-licensed. */
export function markCrmLicenseRevoked(token: string | null): void {
  const key = token ?? '';
  if (revokedToken === key) return;
  revokedToken = key;
  notifyListeners();
}

/** Clears the record. */
export function clearCrmLicenseRevocation(): void {
  if (revokedToken === null) return;
  revokedToken = null;
  notifyListeners();
}

/** True when `currentToken` is the token that was reported unlicensed. */
export function isCrmLicenseRevokedFor(revoked: string | null, currentToken: string | null | undefined): boolean {
  return revoked !== null && revoked === (currentToken ?? '');
}

/** Subscribes the calling component to revocation changes; returns the revoked token key (or null). */
export function useCrmLicenseRevokedToken(): string | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
